import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Loader2, Upload, Trash2, ArrowUp, ArrowDown, Images, ImageOff } from "lucide-react";

export default function CompanyPhotos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dims, setDims] = useState({});
  const fileRef = useRef(null);
  const MIN_W = 1600, MIN_H = 1000;
  const dimOf = (p) => {
    const d = dims[p.id];
    if (d) return d;
    if (p.orig_width && p.orig_height) return { w: p.orig_width, h: p.orig_height };
    if (p.width && p.height) return { w: p.width, h: p.height };
    return null;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/company-photos");
      setPhotos(data.photos || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/company-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(data.photos || []);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleActive = async (p) => {
    setBusyId(p.id);
    try {
      const { data } = await api.patch(`/settings/company-photos/${p.id}`, { active: !p.active });
      setPhotos(data.photos || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    } finally { setBusyId(null); }
  };

  const saveAlt = async (p, alt) => {
    if (alt === p.alt) return;
    try {
      const { data } = await api.patch(`/settings/company-photos/${p.id}`, { alt });
      setPhotos(data.photos || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    }
  };

  const saveDescrizione = async (p, descrizione) => {
    if (descrizione === (p.descrizione || "")) return;
    try {
      const { data } = await api.patch(`/settings/company-photos/${p.id}`, { descrizione });
      setPhotos(data.photos || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Eliminare questa immagine?")) return;
    setBusyId(p.id);
    try {
      const { data } = await api.delete(`/settings/company-photos/${p.id}`);
      setPhotos(data.photos || []);
      toast.success("Immagine eliminata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    } finally { setBusyId(null); }
  };

  const move = async (index, dir) => {
    const next = [...photos];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPhotos(next);
    try {
      const { data } = await api.post("/settings/company-photos/reorder", { order: next.map((p) => p.id) });
      setPhotos(data.photos || next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel riordino");
      load();
    }
  };

  const activeCount = photos.filter((p) => p.active).length;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="company-photos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Images className="h-6 w-6 text-primary" /> Immagini azienda</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Foto in rotazione mostrate nella home page pubblica e nella sezione "La nostra azienda" delle proposte (Outlook). Caricale, riordinale, attivale o disattivale. Le immagini disattivate non vengono mostrate.
          </p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="company-photo-file" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="company-photo-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Carica immagine
          </Button>
          <p className="text-[11px] text-muted-foreground mt-1.5 max-w-[240px] text-right ml-auto">Consigliato min <b>{MIN_W}×{MIN_H}px</b>. Verrà impaginata in 16:10 con bordo nero (senza tagli né perdita di risoluzione).</p>
        </div>
      </div>

      {activeCount === 0 && !loading && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 text-sm px-4 py-3" data-testid="company-photos-empty-warn">
          Nessuna immagine attiva: la rotazione risulterà vuota. Attiva almeno un'immagine.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((p, i) => (
            <Card key={p.id} className={`overflow-hidden border-border ${p.active ? "" : "opacity-60"}`} data-testid={`company-photo-${p.id}`}>
              <div className="relative aspect-[16/10] bg-black">
                <img src={p.url} alt={p.alt} className="absolute inset-0 h-full w-full object-contain" data-testid={`company-photo-img-${p.id}`}
                  onLoad={(e) => { const t = e.currentTarget; if (t.naturalWidth) setDims((d) => ({ ...d, [p.id]: { w: t.naturalWidth, h: t.naturalHeight } })); }} />
                {(() => {
                  const d = dimOf(p);
                  if (!d) return null;
                  const low = d.w < MIN_W || d.h < MIN_H;
                  return (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1.5" data-testid={`company-photo-dim-${p.id}`}>
                      <span className={`rounded-full text-[10px] font-semibold px-2 py-1 ${low ? "bg-amber-500 text-black" : "bg-black/60 text-white"}`}>
                        {d.w}×{d.h}px{low ? " · bassa" : ""}
                      </span>
                    </div>
                  );
                })()}
                {!p.active && (
                  <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] px-2 py-1">
                    <ImageOff className="h-3 w-3" /> Disattivata
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Sposta su"
                    className="h-7 w-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30 transition-colors"
                    data-testid={`company-photo-up-${p.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === photos.length - 1} title="Sposta giù"
                    className="h-7 w-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30 transition-colors"
                    data-testid={`company-photo-down-${p.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="p-3 space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Didascalia / testo alternativo</Label>
                  <Input defaultValue={p.alt} onBlur={(e) => saveAlt(p, e.target.value)} data-testid={`company-photo-alt-${p.id}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione (in sovrimpressione)</Label>
                  <Input defaultValue={p.descrizione || ""} placeholder="Descrizione mostrata sotto la didascalia" onBlur={(e) => saveDescrizione(p, e.target.value)} data-testid={`company-photo-desc-${p.id}`} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={p.active} disabled={busyId === p.id} onCheckedChange={() => toggleActive(p)} data-testid={`company-photo-active-${p.id}`} />
                    <span className="text-xs text-muted-foreground">{p.active ? "Attiva" : "Inattiva"}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" disabled={busyId === p.id}
                    onClick={() => remove(p)} data-testid={`company-photo-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
